import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { useMemo, useRef, useCallback } from "react";
import { useAuth } from "../auth";
import { useAlert } from "../alert";
import { LoginResponse } from "./Login/interfaces/login.response";

const LOCAL_API_URL = "http://localhost:3000";
const PRODUCTION_API_HOST = "api.vivasesaude.com.br";

export const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
  const apiUrl = process.env.REACT_APP_API_URL;

  if (isLocalhost && (!apiUrl || apiUrl.includes(PRODUCTION_API_HOST))) {
    return LOCAL_API_URL;
  }

  if (apiUrl) {
    return apiUrl;
  }

  return isLocalhost ? LOCAL_API_URL : undefined;
};

const baseUrl = getApiBaseUrl();

export const useApi = () => {
  const { getToken, signIn, signOut } = useAuth();
  const { error: errorAlert } = useAlert();

  const getTokenRef = useRef(getToken);
  const signInRef = useRef(signIn);
  const signOutRef = useRef(signOut);
  const errorAlertRef = useRef(errorAlert);

  getTokenRef.current = getToken;
  signInRef.current = signIn;
  signOutRef.current = signOut;
  errorAlertRef.current = errorAlert;

  const api = useMemo(() => {
    let isRefreshing = false;
    let refreshSubscribers: Array<(token: string) => void> = [];

    const onRefreshed = (token: string) => {
      refreshSubscribers.forEach((cb) => cb(token));
      refreshSubscribers = [];
    };

    const addRefreshSubscriber = (cb: (token: string) => void) => {
      refreshSubscribers.push(cb);
    };

    const instance = axios.create({
      baseURL: baseUrl,
    });

    instance.interceptors.request.use(
      (config) => {
        const token = getTokenRef.current();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    instance.interceptors.response.use(
      (response) => response,
      (err) => {
        const originalReq = err.config;
        console.log(err);

        if (
          err?.response?.status === 401 &&
          err?.config &&
          !err?.config?._retry
        ) {
          originalReq._retry = true;

          if (isRefreshing) {
            return new Promise((resolve) => {
              addRefreshSubscriber((newToken: string) => {
                originalReq.headers["Authorization"] = `Bearer ${newToken}`;
                resolve(axios(originalReq));
              });
            });
          }

          isRefreshing = true;
          const token = getTokenRef.current();

          if (!token) {
            isRefreshing = false;
            return Promise.reject(err);
          }

          return instance
            .put<LoginResponse>(`${baseUrl}/token/refresh`, {
              oldToken: token,
            })
            .then((res) => {
              const newToken = res?.data?.accessToken;
              signInRef.current(newToken, res?.data.user);
              isRefreshing = false;
              onRefreshed(newToken);
              originalReq.headers["Authorization"] = `Bearer ${newToken}`;
              return axios(originalReq);
            })
            .catch((error) => {
              console.log("Refresh err:", error);
              isRefreshing = false;
              refreshSubscribers = [];
              errorAlertRef.current(
                "Houve um erro ao atualizar o token, retornando ao login!"
              );
              signOutRef.current();
              return Promise.reject(error);
            });
        }

        return Promise.reject(err);
      }
    );

    return instance;
  }, []);

  const get = useCallback(
    <T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>) =>
      api.get<T, R, D>(url, config),
    [api]
  );
  const del = useCallback(
    <T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>) =>
      api.delete<T, R, D>(url, config),
    [api]
  );
  const post = useCallback(
    <T = any, R = AxiosResponse<T>, D = any>(
      url: string,
      data?: D,
      config?: AxiosRequestConfig<D>
    ) => api.post<T, R, D>(url, data, config),
    [api]
  );
  const put = useCallback(
    <T = any, R = AxiosResponse<T>, D = any>(
      url: string,
      data?: D,
      config?: AxiosRequestConfig<D>
    ) => api.put<T, R, D>(url, data, config),
    [api]
  );
  const patch = useCallback(
    <T = any, R = AxiosResponse<T>, D = any>(
      url: string,
      data?: D,
      config?: AxiosRequestConfig<D>
    ) => api.patch<T, R, D>(url, data, config),
    [api]
  );

  return {
    get,
    del,
    post,
    put,
    patch,
  };
};
